// CycleCount.jsx — Warehouse portal: Cycle Count tool
// Build a SKU checklist, enter physical counts per SKU, submit for review + apply.
import React, {
    useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getPresserSession, pressingFetch, authenticatedFetch, useAuth,
} from './AuthComponent';
import { useStore } from './StoreContext';
import useConsoleLocations, { WAREHOUSE_V2 } from './useConsoleLocations';
import InvDiscardSheet from './InvDiscardSheet';
import InvQtyActions from './InvQtyActions';
import { useScanAlert, useScannerCapture } from './InvScanKit';
import { hasRole } from './constants/roles';
import { exportReviewCsv } from './invExport';

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
    shell:       { minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column', position: 'relative' },
    bar:         { background: '#1e3a5f', padding: 'calc(16px + env(safe-area-inset-top, 0px)) 16px 12px', color: '#fff', flexShrink: 0 },
    barRow:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    barTitle:    { fontSize: 20, fontWeight: 800, letterSpacing: 1 },
    barSub:      { fontSize: 12, color: '#93c5fd', marginTop: 2 },
    backBtn:     { background: 'none', border: 'none', color: '#93c5fd', fontSize: 13,
                   cursor: 'pointer', padding: '4px 0', marginTop: 4 },
    body:        { flex: 1, overflow: 'auto', padding: '0 0 80px' },
    scanCard:    { background: '#fff', margin: '12px 12px 0', borderRadius: 10,
                   padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,.07)' },
    scanLabel:   { fontSize: 12, color: '#6b7280', marginBottom: 6, fontWeight: 600 },
    scanRow:     { display: 'flex', gap: 8 },
    scanInput:   { flex: 1, fontSize: 16, padding: '10px 12px', border: '2px solid #3b82f6',
                   borderRadius: 8, outline: 'none', background: '#f0f9ff' },
    addBtn:      { padding: '10px 16px', background: '#1e3a5f', color: '#fff',
                   border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    addBtnDis:   { background: '#6b7280' },
    errCard:     { margin: '8px 12px 0', background: '#fee2e2', borderRadius: 8,
                   padding: '10px 14px', fontSize: 13, color: '#991b1b' },
    lineSection: { margin: '12px 12px 0' },
    lineHdr:     { fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 6,
                   textTransform: 'uppercase', letterSpacing: .5 },
    lineCard:    { background: '#fff', borderRadius: 10, padding: '10px 12px',
                   marginBottom: 8, boxShadow: '0 1px 3px rgba(0,0,0,.06)' },
    lineTop:     { display: 'flex', alignItems: 'center', gap: 10 },
    lineInfo:    { flex: 1, overflow: 'hidden' },
    lineSku:     { fontSize: 13, fontWeight: 700, color: '#111827',
                   overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    lineTitle:   { fontSize: 11, color: '#9ca3af',
                   overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    removeBtn:   { background: 'none', border: 'none', color: '#d1d5db',
                   fontSize: 18, cursor: 'pointer', padding: '2px 4px', lineHeight: 1 },
    countRow:    { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 },
    countLabel:  { fontSize: 12, color: '#6b7280', fontWeight: 600, minWidth: 50 },
    countStepper:{ display: 'flex', alignItems: 'center', gap: 0, border: '1.5px solid #e5e7eb',
                   borderRadius: 8, overflow: 'hidden' },
    stepBtn:     { width: 36, height: 36, border: 'none', background: '#f9fafb',
                   fontSize: 20, cursor: 'pointer', color: '#374151',
                   display: 'flex', alignItems: 'center', justifyContent: 'center' },
    countInputW: { width: 52, border: 'none', textAlign: 'center', fontSize: 18,
                   fontWeight: 700, outline: 'none', background: '#fff', padding: '6px 0' },
    countBadge:  (done) => ({
        marginLeft: 'auto', width: 20, height: 20, borderRadius: '50%',
        background: done ? '#10b981' : '#e5e7eb',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff',
    }),
    bottomBar:   { position: 'sticky', bottom: 0,
                   background: '#fff', borderTop: '1px solid #e5e7eb',
                   padding: '10px 16px', display: 'flex', alignItems: 'center',
                   justifyContent: 'space-between', boxShadow: '0 -2px 8px rgba(0,0,0,.06)' },
    countPill:   { fontSize: 13, color: '#6b7280', fontWeight: 600 },
    submitBtn:   { padding: '11px 28px', background: '#059669', color: '#fff',
                   border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
    submitBtnDis:{ background: '#9ca3af', cursor: 'not-allowed' },
    // review / applied share AddInventory styles
    revCard:     { background: '#fff', margin: '12px 12px 0', borderRadius: 10,
                   overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.07)' },
    revTable:    { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    revTh:       { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb',
                   fontWeight: 700, color: '#374151', fontSize: 12 },
    revTd:       { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
    applyBtn:    { padding: '12px 32px', background: '#1e3a5f', color: '#fff',
                   border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
    applyBtnDis: { background: '#9ca3af', cursor: 'not-allowed' },
    undoBtn:     { padding: '11px 18px', fontSize: 14, background: '#f3f4f6',
                   border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer',
                   color: '#374151', fontWeight: 600 },
    successWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center',
                   padding: '48px 24px', gap: 12, textAlign: 'center' },
    overlay:     { position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 600,
                   display: 'flex', alignItems: 'flex-end' },
    sheet:       { background: '#fff', borderRadius: '16px 16px 0 0', padding: '20px 16px 32px',
                   width: '100%', maxHeight: '60vh', overflowY: 'auto' },
    sheetTitle:  { fontSize: 17, fontWeight: 700, marginBottom: 14, color: '#111827' },
    locBtn:      { width: '100%', textAlign: 'left', padding: '12px 14px', margin: '4px 0',
                   border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
                   fontSize: 14, cursor: 'pointer', fontWeight: 600, color: '#1e3a5f' },
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function CycleCount({ consoleMode = false }) {
    const navigate        = useNavigate();
    const { user }        = useAuth();
    const { activeStore } = useStore();
    const consoleLocs = useConsoleLocations(consoleMode);   // null while loading; already scoped to the user and the sidebar location
    const [session] = useState(() => consoleMode ? null : getPresserSession());

    // Location state
    const [locationId,    setLocationId]    = useState(null);
    const [locationName,  setLocationName]  = useState('');
    const [storeKey,      setStoreKey]      = useState(null);
    const [showLocPicker, setShowLocPicker] = useState(false);

    // Session state
    const [sessionId,   setSessionId]   = useState(null);
    const [sessionChecked, setSessionChecked] = useState(false);   // the open-session lookup for this location has finished
    const [lastItem,   setLastItem]   = useState(null);   // {sku,title}: the item just scanned, for Set / Add quantity
    const [qtyBusy,    setQtyBusy]    = useState(false);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [discarding,      setDiscarding]      = useState(false);
    const [screen,      setScreen]      = useState('count'); // 'count'|'review'|'applied'
    // lines during count: {sku, title, variant_gid?, count (local), saving, saved}
    const [lines,       setLines]       = useState([]);
    // lines during review: full server objects
    const [reviewLines, setReviewLines] = useState([]);
    const [scanInput,   setScanInput]   = useState('');
    const [adding,      setAdding]      = useState(false);
    const [submitting,  setSubmitting]  = useState(false);
    const [applying,    setApplying]    = useState(false);
    const [scanError,   setScanError]   = useState(null);
    const [applyResult, setApplyResult] = useState(null);
    const [breakdown,   setBreakdown]   = useState(null);   // Review: {skus:{sku:{scans,adjustment,...}}, unmatched}
    const [openSkus,    setOpenSkus]    = useState({});     // Review: which SKU rows are expanded

    const scanRef   = useRef(null);
    const { alertMsg, raise: raiseScanAlert, alertNode } = useScanAlert(scanRef);   // invalid scan: red flash + buzz + tap-to-dismiss notice
    // Debounce timers per SKU for set_count calls

    // ── Auth / location setup ─────────────────────────────────────────────
    useEffect(() => {
        if (consoleMode) {
            if (!user) navigate('/login', { replace: true });
        } else {
            if (!session) { navigate('/pressing-portal/login', { replace: true }); return; }
            if (!session.roles?.includes('warehouse')) { navigate('/pressing-portal/login', { replace: true }); return; }
        }
    }, [consoleMode, user, session, navigate]);

    const myAssignments = useMemo(() => {
        if (!consoleMode) return session?.assignments || [];
        if (!Array.isArray(consoleLocs)) return [];
        return consoleLocs.map(l => ({ shopify_location_id: l.shopify_location_id, location_name: l.name, store_key: activeStore }));
    }, [consoleMode, session, consoleLocs, activeStore]);

    useEffect(() => {
        if (consoleMode && consoleLocs === null) return;
        if (myAssignments.length === 1 && !locationId) {
            setLocationId(myAssignments[0].shopify_location_id);
            setLocationName(myAssignments[0].location_name || '');
            setStoreKey(myAssignments[0].store_key);
        } else if (myAssignments.length > 1 && !locationId) {
            setShowLocPicker(true);
        }
    }, [myAssignments, locationId, consoleMode, consoleLocs]);

    // The sidebar location (or the user's access) changed under us: drop a location that is no longer offered.
    useEffect(() => {
        if (!consoleMode || consoleLocs === null || !locationId) return;
        if (myAssignments.some(a => String(a.shopify_location_id) === String(locationId))) return;
        setLocationId(null); setLocationName(''); setStoreKey(null);
        setSessionId(null); setLines([]); setLastItem(null); setScreen('count'); setShowLocPicker(false);
    }, [consoleMode, consoleLocs, myAssignments, locationId]);

    // ── Permission check ─────────────────────────────────────────────────
    // canCreate = may START a Cycle Count: a PIN with warehouse.create_cycle_count, or console admin/site_manager.
    // v2 (test site): anyone else can still JOIN one that is open.
    const canCreate = useMemo(() => {
        if (consoleMode) return hasRole(user, ['admin', 'site_manager']);
        return (session?.roles || []).includes('warehouse.create_cycle_count');
    }, [consoleMode, user, session]);

    // ── Fetch helper ──────────────────────────────────────────────────────
    const invFetch = useMemo(() => {
        if (consoleMode) return (path, opts = {}) => authenticatedFetch(path, opts);
        const pid = session?.presser_id;
        return (path, opts = {}) => {
            const sep = path.includes('?') ? '&' : '?';
            return pressingFetch(`${path}${sep}presser_id=${encodeURIComponent(pid)}`, opts);
        };
    }, [consoleMode, session]);

    // ── Session management ────────────────────────────────────────────────
    useEffect(() => {
        if (!locationId || !storeKey) return;
        let live = true;
        setSessionChecked(false);
        invFetch(`/api/inv/${storeKey}/sessions?shopify_location_id=${encodeURIComponent(locationId)}&session_type=cycle_count`)
            .then(r => r.ok ? r.json() : { sessions: [] })
            .then(j => {
                if (!live) return;
                const open      = (j.sessions || []).find(s => s.status === 'open');
                const submitted = (j.sessions || []).find(s => s.status === 'submitted');
                if (submitted) {
                    setSessionId(submitted.id);
                    invFetch(`/api/inv/${storeKey}/sessions/${submitted.id}?shopify_location_id=${encodeURIComponent(locationId)}`)
                        .then(r2 => r2.ok ? r2.json() : null)
                        .then(j2 => { if (live && j2) { setReviewLines(j2.lines || []); setScreen('review'); } });
                } else if (open) {
                    setSessionId(open.id);
                    invFetch(`/api/inv/${storeKey}/sessions/${open.id}?shopify_location_id=${encodeURIComponent(locationId)}`)
                        .then(r2 => r2.ok ? r2.json() : null)
                        .then(j2 => {
                            if (!live || !j2) return;
                            setLines((j2.lines || []).map(l => ({ sku: l.sku, title: null, variant_gid: l.variant_gid, count: l.scanned, saved: true })));
                        });
                }
                setSessionChecked(true);
            })
            .catch(() => {});
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId, storeKey]);

    const ensureSession = useCallback(async () => {
        if (sessionId) return sessionId;
        if (WAREHOUSE_V2 && !canCreate) throw new Error('No Cycle Count is open here. Someone with start rights has to start one.');
        const r = await invFetch(`/api/inv/${storeKey}/sessions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ shopify_location_id: locationId, session_type: 'cycle_count' }),
        });
        if (!r.ok) {
            const j = await r.json();
            throw new Error(j.detail || 'Failed to create session');
        }
        const j = await r.json();
        setSessionId(j.session_id);
        return j.session_id;
    }, [sessionId, canCreate, storeKey, locationId, invFetch]);

    // ── Add SKU to checklist ──────────────────────────────────────────────
    const handleAdd = useCallback(async (code) => {
        const barcode = (typeof code === 'string' ? code : scanInput).trim();   // a captured scanner code, or what was typed
        if (!barcode || !locationId || !storeKey || adding) return;
        setScanInput('');
        setAdding(true);
        setScanError(null);
        try {
            const sid = await ensureSession();
            // Use the scan endpoint to resolve barcode → SKU (qty=0 would be weird;
            // we use qty=1 then immediately set count via PATCH to preserve the "scan adds to list"
            // semantics while giving the user a count input to adjust).
            const r = await invFetch(
                `/api/inv/${storeKey}/sessions/${sid}/scan?shopify_location_id=${encodeURIComponent(locationId)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ barcode, qty: 1 }),
                },
            );
            const j = await r.json();
            if (!r.ok) { raiseScanAlert(j.detail || 'Add failed'); return; }
            if (!j.matched) { raiseScanAlert(`SKU not found — "${barcode}"`); return; }

            const sku = j.sku;
            setLastItem({ sku, title: j.title });
            setLines(prev => {
                // already in the list: the scan added one on the server, so show the server's count
                if (prev.find(l => l.sku === sku)) return prev.map(l => l.sku === sku ? { ...l, count: j.line_scanned ?? l.count + 1, saved: true } : l);
                return [{ sku, title: j.title, variant_gid: null, count: 1, saved: true }, ...prev];
            });
        } catch (e) {
            raiseScanAlert(e.message || 'Network error');
        } finally {
            setAdding(false);
        }
    }, [scanInput, locationId, storeKey, adding, ensureSession, invFetch]);

    // ── Set count (debounced PATCH) ───────────────────────────────────────
    const saveCount = useCallback(async (sku, count, sid) => {
        const url = `/api/inv/${storeKey}/sessions/${sid}/lines/${encodeURIComponent(sku)}`
            + `?shopify_location_id=${encodeURIComponent(locationId)}`;
        await invFetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scanned: count }),
        });
        setLines(prev => prev.map(l => l.sku === sku ? { ...l, saved: true } : l));
    }, [storeKey, locationId, invFetch]);

    const handleRemoveSku = useCallback(async (sku) => {
        setLines(prev => prev.filter(l => l.sku !== sku));
        if (!sessionId) return;
        const url = `/api/inv/${storeKey}/sessions/${sessionId}/lines/${encodeURIComponent(sku)}`
            + `?shopify_location_id=${encodeURIComponent(locationId)}`;
        invFetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scanned: 0 }),
        }).catch(() => {});
    }, [sessionId, storeKey, locationId, invFetch]);

    // Flush unsaved counts before submitting
    const flushCounts = useCallback(async (sid) => {
        const unsaved = lines.filter(l => !l.saved);
        await Promise.all(unsaved.map(l => saveCount(l.sku, l.count, sid)));
    }, [lines, saveCount]);

    // ── Set / Add a typed quantity for the item just scanned ──────────────
    const applyQty = useCallback(async (mode, n) => {
        if (!lastItem || qtyBusy || !locationId || !storeKey) return;
        const cur    = lines.find(l => l.sku === lastItem.sku)?.count || 0;
        const target = mode === 'add' ? cur + n : n;
        setQtyBusy(true);
        setScanError(null);
        try {
            const sid = await ensureSession();
            const r = await invFetch(
                `/api/inv/${storeKey}/sessions/${sid}/lines/${encodeURIComponent(lastItem.sku)}`
                + `?shopify_location_id=${encodeURIComponent(locationId)}`,
                { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ scanned: target, entry: mode }) },
            );
            const j = await r.json().catch(() => ({}));
            if (!r.ok) { raiseScanAlert(j.detail || 'Could not save the quantity'); return; }
            setLines(prev => prev.map(l => l.sku === lastItem.sku ? { ...l, count: target, saved: true } : l));
        } catch (e) {
            raiseScanAlert(e.message || 'Network error');
        } finally {
            setQtyBusy(false);
        }
    }, [lastItem, qtyBusy, locationId, storeKey, lines, ensureSession, invFetch]);

    // The scan box always has focus (scanners type into it); not while a picker / confirmation / notice is up.
    useScannerCapture(handleAdd, screen === 'count' && !!locationId && !showLocPicker && !confirmDiscard && !alertMsg && !(WAREHOUSE_V2 && !canCreate && !sessionId));

    // ── Submit ────────────────────────────────────────────────────────────
    const handleSubmit = useCallback(async () => {
        if (!sessionId || !locationId || submitting) return;
        setSubmitting(true);
        setScanError(null);
        try {
            await flushCounts(sessionId);
            const r = await invFetch(`/api/inv/${storeKey}/sessions/${sessionId}/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ shopify_location_id: locationId }),
            });
            const j = await r.json();
            if (!r.ok) { setScanError(j.detail || 'Submit failed'); return; }
            setReviewLines(j.lines || []);
            setScreen('review');
        } catch (e) {
            setScanError(e.message);
        } finally {
            setSubmitting(false);
        }
    }, [sessionId, storeKey, locationId, submitting, flushCounts, invFetch]);

    // ── Apply ─────────────────────────────────────────────────────────────
    // ── Discard (abandon without applying) ────────────────────────────────
    const handleDiscard = useCallback(async () => {
        if (!sessionId || !locationId || discarding) return;
        setDiscarding(true);
        setScanError(null);
        try {
            const r = await invFetch(
                `/api/inv/${storeKey}/sessions/${sessionId}?shopify_location_id=${encodeURIComponent(locationId)}`,
                { method: 'DELETE' },
            );
            const j = await r.json().catch(() => ({}));
            if (!r.ok) { setScanError(j.detail || 'Could not discard the count'); return; }
            setSessionId(null); setLines([]); setLastItem(null); setReviewLines([]); setScreen('count');
        } catch (e) {
            setScanError(e.message || 'Network error');
        } finally {
            setDiscarding(false);
            setConfirmDiscard(false);
        }
    }, [sessionId, storeKey, locationId, discarding, invFetch]);

    const handleApply = useCallback(async () => {
        if (!sessionId || !locationId || applying) return;
        setApplying(true);
        setScanError(null);
        try {
            const r = await invFetch(`/api/inv/${storeKey}/sessions/${sessionId}/apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ shopify_location_id: locationId }),
            });
            const j = await r.json();
            if (!r.ok || !j.ok) {
                setScanError(j.errors?.map(e => `${e.sku}: ${e.error}`).join('; ') || j.detail || 'Apply failed');
                return;
            }
            setApplyResult(j);
            setScreen('applied');
        } catch (e) {
            setScanError(e.message);
        } finally {
            setApplying(false);
        }
    }, [sessionId, storeKey, locationId, applying, invFetch]);

    // Review: who scanned what quantity, and when, for each SKU total.
    useEffect(() => {
        if (screen !== 'review' || !sessionId || !storeKey || !locationId) return;
        let live = true;
        invFetch(`/api/inv/${storeKey}/sessions/${sessionId}/scans?shopify_location_id=${encodeURIComponent(locationId)}`)
            .then(r => r.ok ? r.json() : null)
            .then(j => { if (live && j) setBreakdown(j); })
            .catch(() => {});
        return () => { live = false; };
    }, [screen, sessionId, storeKey, locationId, invFetch]);

    const canApply = useMemo(
        () => consoleMode && hasRole(user, ['admin', 'site_manager']),
        [consoleMode, user],
    );

    const countedSkus = useMemo(() => lines.filter(l => l.count > 0).length, [lines]);

    // ── Header ────────────────────────────────────────────────────────────
    const renderHeader = (sub) => (
        <div style={S.bar}>
            <div style={S.barRow}>
                <div>
                    <div style={S.barTitle}>CYCLE COUNT</div>
                    <div style={S.barSub}>{locationName || sub || 'Select location'}</div>
                </div>
                {locationId && myAssignments.length > 1 && (
                    <button style={S.backBtn} onClick={() => setShowLocPicker(true)}>📍 Change</button>
                )}
            </div>
            <button style={S.backBtn} onClick={() => navigate('../home', { relative: 'path' })}>
                ← Back to menu
            </button>
        </div>
    );

    // ── Permission denied ─────────────────────────────────────────────────
    if (!WAREHOUSE_V2 && locationId && !canCreate) {
        return (
            <div style={S.shell}>
                {renderHeader()}
                <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#374151', marginBottom: 6 }}>
                        Permission required
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                        This PIN does not have permission to create Cycle Counts.
                    </div>
                </div>
            </div>
        );
    }

    // ── Count screen ──────────────────────────────────────────────────────
    if (screen === 'count') {
        return (
            <div style={S.shell}>
                {renderHeader()}
                {alertNode}
                {confirmDiscard && (
                    <InvDiscardSheet
                        title="Discard this Cycle Count?"
                        detail="The counts entered so far are thrown away and nothing is changed in Shopify. You can start a new count afterwards."
                        busy={discarding}
                        onKeep={() => setConfirmDiscard(false)}
                        onDiscard={handleDiscard} />
                )}
                <div style={S.body}>
                    {/* Nothing to join: only people with start rights begin a Cycle Count */}
                    {WAREHOUSE_V2 && !canCreate && locationId && sessionChecked && !sessionId && (
                        <div style={{ ...S.errCard, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>
                            No Cycle Count is open at this location. A Site Manager, Admin or a PIN with start rights has to start one before you can join it.
                        </div>
                    )}

                    {/* Add SKU input */}
                    <div style={{ ...S.scanCard, ...((WAREHOUSE_V2 && !canCreate && !sessionId) ? { display: 'none' } : {}) }}>
                        <div style={S.scanLabel}>ADD SKU TO COUNT LIST</div>
                        <div style={S.scanRow}>
                            <input
                                ref={scanRef}
                                style={S.scanInput}
                                value={scanInput}
                                onChange={e => { setScanInput(e.target.value); setScanError(null); }}
                                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                                placeholder={lastItem ? 'Scan next item…' : 'Scan barcode or type SKU…'}
                                inputMode="text"
                                autoComplete="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                disabled={adding || !!alertMsg || !locationId}
                            />
                            <button
                                style={{ ...S.addBtn, ...(adding ? S.addBtnDis : {}) }}
                                onClick={handleAdd}
                                disabled={adding || qtyBusy || !scanInput.trim() || !locationId}
                            >
                                {adding ? '…' : '+ Add'}
                            </button>
                        </div>
                    </div>

                    <InvQtyActions item={lastItem} count={lines.find(l => l.sku === lastItem?.sku)?.count || 0}
                                   busy={qtyBusy} onApply={applyQty} />

                    {scanError && <div style={S.errCard}>{scanError}</div>}

                    {/* SKU checklist */}
                    {lines.length > 0 && (
                        <div style={S.lineSection}>
                            <div style={S.lineHdr}>Count list ({lines.length} SKU{lines.length !== 1 ? 's' : ''})</div>
                            {lines.map(line => (
                                <div key={line.sku} style={S.lineCard}>
                                    <div style={S.lineTop}>
                                        <div style={S.lineInfo}>
                                            <div style={S.lineSku}>{line.sku}</div>
                                            {line.title && <div style={S.lineTitle}>{line.title}</div>}
                                        </div>
                                        <div style={S.countBadge(line.count > 0)}>
                                            {line.count > 0 ? '✓' : ''}
                                        </div>
                                        <button style={S.removeBtn} onClick={() => handleRemoveSku(line.sku)} title="Remove">×</button>
                                    </div>
                                    <div style={S.countRow}>
                                        <span style={S.countLabel}>Count:</span>
                                        <span style={{ fontSize: 18, fontWeight: 800, color: '#1e3a5f' }}>{line.count}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {lines.length === 0 && locationId && (
                        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#9ca3af' }}>
                            <div style={{ fontSize: 40, marginBottom: 10 }}>🔄</div>
                            <div style={{ fontWeight: 600, color: '#6b7280' }}>Add SKUs to count</div>
                            <div style={{ fontSize: 13, marginTop: 4 }}>Scan or type a barcode above</div>
                        </div>
                    )}
                </div>

                {/* Bottom bar */}
                <div style={S.bottomBar}>
                    <div style={S.countPill}>
                        {lines.length > 0
                            ? `${countedSkus}/${lines.length} counted`
                            : 'No SKUs yet'}
                    </div>
                    {WAREHOUSE_V2 && sessionId && canCreate && (
                        <button style={{ ...S.undoBtn, marginLeft: 0, color: '#b91c1c', borderColor: '#fecaca' }}
                                onClick={() => setConfirmDiscard(true)}>
                            Discard count
                        </button>
                    )}
                    <button
                        style={{ ...S.submitBtn, ...(lines.length === 0 || submitting ? S.submitBtnDis : {}) }}
                        onClick={handleSubmit}
                        disabled={lines.length === 0 || submitting}
                    >
                        {submitting ? 'Submitting…' : 'Review →'}
                    </button>
                </div>

                {/* Location picker */}
                {showLocPicker && (
                    <div style={S.overlay} onClick={() => { if (locationId) setShowLocPicker(false); }}>
                        <div style={S.sheet} onClick={e => e.stopPropagation()}>
                            <div style={S.sheetTitle}>Select your location</div>
                            {myAssignments.map(a => (
                                <button key={a.shopify_location_id} style={S.locBtn} onClick={() => {
                                    setLocationId(a.shopify_location_id);
                                    setLocationName(a.location_name || '');
                                    setStoreKey(a.store_key);
                                    setSessionId(null);
                                    setLines([]); setLastItem(null);
                                    setScreen('count');
                                    setShowLocPicker(false);
                                }}>
                                    {a.location_name || a.shopify_location_id}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── Review screen ─────────────────────────────────────────────────────
    if (screen === 'review') {
        const linesWithVariant = reviewLines.filter(l => l.variant_gid);
        const changedLines = linesWithVariant.filter(l => {
            const eff = (l.scanned || 0) + (l.picked_added || 0) + (l.overstock_counted || 0);
            return l.live_on_hand !== null && l.live_on_hand !== undefined && eff !== l.live_on_hand;
        });
        return (
            <div style={S.shell}>
                {renderHeader('Review counts')}
                {confirmDiscard && (
                    <InvDiscardSheet
                        title="Discard this Cycle Count?"
                        detail="The counts entered so far are thrown away and nothing is changed in Shopify. You can start a new count afterwards."
                        busy={discarding}
                        onKeep={() => setConfirmDiscard(false)}
                        onDiscard={handleDiscard} />
                )}
                <div style={S.body}>
                    {scanError && <div style={{ ...S.errCard, margin: '12px 12px 0' }}>{scanError}</div>}
                    <div style={{ padding: '12px 16px 0', fontSize: 13, color: '#6b7280' }}>
                        {changedLines.length} of {linesWithVariant.length} SKU{linesWithVariant.length !== 1 ? 's' : ''} will be updated.
                    </div>
                    <div style={S.revCard}>
                        <table style={S.revTable}>
                            <thead>
                                <tr>
                                    <th style={S.revTh}>SKU</th>
                                    <th style={{ ...S.revTh, textAlign: 'right' }}>Counted</th>
                                    <th style={{ ...S.revTh, textAlign: 'right' }}>Picked</th>
                                    <th style={{ ...S.revTh, textAlign: 'right' }}>Effective</th>
                                    <th style={{ ...S.revTh, textAlign: 'right' }}>Was</th>
                                    <th style={{ ...S.revTh, textAlign: 'right' }}>Δ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviewLines.map(line => {
                                    const eff   = (line.scanned || 0) + (line.picked_added || 0) + (line.overstock_counted || 0);
                                    const was   = line.live_on_hand;
                                    const delta = (was !== null && was !== undefined) ? eff - was : null;
                                    const dColor = delta === null ? '#9ca3af'
                                                 : delta > 0    ? '#065f46'
                                                 : delta < 0    ? '#991b1b'
                                                 : '#9ca3af';
                                    const bd = breakdown?.skus?.[line.sku];
                                    const isOpen = !!openSkus[line.sku];
                                    return (
                                        <React.Fragment key={line.sku}>
                                        <tr>
                                            <td style={{ ...S.revTd, fontWeight: 600, fontSize: 12, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: bd ? 'pointer' : 'default' }}
                                                onClick={() => bd && setOpenSkus(o => ({ ...o, [line.sku]: !o[line.sku] }))}>
                                                {bd ? (isOpen ? '▾ ' : '▸ ') : ''}{line.sku}
                                            </td>
                                            <td style={{ ...S.revTd, textAlign: 'right' }}>{line.scanned || 0}</td>
                                            <td style={{ ...S.revTd, textAlign: 'right', color: '#6b7280' }}>{line.picked_added || 0}</td>
                                            <td style={{ ...S.revTd, textAlign: 'right', fontWeight: 700 }}>{eff}</td>
                                            <td style={{ ...S.revTd, textAlign: 'right', color: '#6b7280' }}>
                                                {was !== null && was !== undefined ? was : '—'}
                                            </td>
                                            <td style={{ ...S.revTd, textAlign: 'right', fontWeight: 700, color: dColor }}>
                                                {delta === null ? '—' : delta === 0 ? '=' : delta > 0 ? `+${delta}` : delta}
                                            </td>
                                        </tr>
                                        {isOpen && bd && (
                                            <tr>
                                                <td colSpan={6} style={{ ...S.revTd, background: '#f9fafb', fontSize: 12 }}>
                                                    {bd.users.map(u => (
                                                        <div key={u.user} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0' }}>
                                                            <span>{u.user}</span>
                                                            <span>
                                                                {u.scanned > 0 && <>Scanned <b>{u.scanned}</b></>}
                                                                {u.scanned > 0 && u.entered !== 0 && ' · '}
                                                                {u.entered !== 0 && <>Entered <b>{u.entered > 0 ? `+${u.entered}` : u.entered}</b></>}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    {bd.adjustment !== 0 && (
                                                        <div style={{ padding: '2px 0', color: '#92400e' }}>
                                                            Entered by someone (not attributed) / removed scans: {bd.adjustment > 0 ? `+${bd.adjustment}` : bd.adjustment}
                                                        </div>
                                                    )}
                                                    {bd.users.length === 0 && bd.adjustment === 0 && <div style={{ color: '#6b7280' }}>No scans recorded</div>}
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ display: 'flex', gap: 12, padding: '16px 12px', flexWrap: 'wrap' }}>
                        <button style={S.undoBtn} onClick={() => setScreen('count')}>← Back to counting</button>
                        <button style={S.undoBtn} disabled={!breakdown}
                                onClick={() => exportReviewCsv(reviewLines, breakdown, `cycle-count-${sessionId}-review.csv`)}>
                            Export CSV
                        </button>
                        {WAREHOUSE_V2 && canCreate && (
                            <button style={{ ...S.undoBtn, marginLeft: 0, color: '#b91c1c', borderColor: '#fecaca' }}
                                    onClick={() => setConfirmDiscard(true)}>
                                Discard count
                            </button>
                        )}
                        {canApply ? (
                            <button
                                style={{ ...S.applyBtn, ...(applying || changedLines.length === 0 ? S.applyBtnDis : {}), flex: 1 }}
                                onClick={handleApply}
                                disabled={applying || changedLines.length === 0}
                            >
                                {applying ? 'Applying…' : `Apply ${changedLines.length} change${changedLines.length !== 1 ? 's' : ''}`}
                            </button>
                        ) : (
                            <div style={{ flex: 1, background: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '11px 14px', fontSize: 13, color: '#1d4ed8' }}>
                                ℹ️ Submitted — awaiting manager approval to apply
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── Applied screen ────────────────────────────────────────────────────
    const changed = (applyResult?.results || []).filter(r => r.ok).length;
    const skipped = (applyResult?.results || []).filter(r => r.skipped).length;
    return (
        <div style={S.shell}>
            {renderHeader('Applied')}
            <div style={S.body}>
                <div style={S.successWrap}>
                    <div style={{ fontSize: 56 }}>✅</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>Cycle count applied</div>
                    <div style={{ fontSize: 14, color: '#6b7280', maxWidth: 260 }}>
                        {changed} SKU{changed !== 1 ? 's' : ''} updated{skipped > 0 ? `, ${skipped} unchanged` : ''}.
                    </div>
                    <button
                        style={{ marginTop: 16, padding: '12px 32px', background: '#1e3a5f', color: '#fff',
                                 border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
                        onClick={() => navigate('../home', { relative: 'path' })}
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
